/**
 * indexedDB/eviction.ts
 *
 * LRU page-cache eviction for IndexedDB blocks.
 *
 * Strategy:
 *   - Track one `pageCache` record per visited page (pageId + lastAccessedAt + blockIds).
 *   - On every remote fetch (upsertBlocksMutation), run eviction lazily in the background.
 *   - Evict by three rules in order:
 *       1. TTL  — pages not accessed in > 30 days.
 *       2. Page count  — keep at most MAX_PAGES (50) pages cached.
 *       3. Block count — if still > MAX_BLOCKS (10,000), keep evicting oldest pages.
 *   - Reference check (applied after all three rules):
 *       A page whose pageId appears in any surviving page's blockIds array is a child
 *       page still referenced by a cached parent. It is removed from the evict set
 *       regardless of its LRU score — evicting it would leave the parent with a broken
 *       block reference in IDB. Once the parent itself is evicted, the child loses its
 *       protection and becomes a normal LRU candidate on the next eviction pass.
 *   - Never evict the currently open page.
 *   - Never evict blocks with isSync: 0 (pending sync to server).
 *   - All errors are swallowed — eviction is background cleanup, never blocks the UI.
 */

import { appDB } from "./db/client";
import type { PageCacheRecord } from "./db/schema";
import { isIDBAvailable } from "./idbStatus";
import { getPendingBlockIds } from "./stores/syncQueueStore";
import { formatInstant } from "@/lib/timezone";

// ── Sidebar-based orphan cleanup ──────────────────────────────────────────────

/**
 * Remove root page blocks from IDB that are no longer present in the
 * sidebar-initial API response.
 *
 * When to call: after a successful sidebar-initial fetch, fire-and-forget.
 *
 * Which blocks are considered "orphan root pages":
 *   - parentType === "workspace" OR parentType === "workarea"  (sidebar-level pages)
 *   - isSync === 1  (already confirmed by the server — safe to remove)
 *   - _id is NOT in the set of page IDs returned by the API
 *
 * For each orphan:
 *   - If a pageCache entry exists, delete all its tracked content blocks
 *     (skipping any with isSync: 0 that haven't synced yet).
 *   - Always delete the root page block itself.
 *   - Delete the pageCache entry.
 *
 * All errors are swallowed — this is background cleanup and must never
 * block or crash the UI.
 */
export async function purgeOrphanRootPages(
  workspaceId: string,
  apiPageIds: Set<string>
): Promise<void> {
  // Short-circuit when IDB is unavailable — there's nothing to purge and
  // attempting the query would just throw a DOMException that gets logged.
  if (!isIDBAvailable()) return;
  try {
    // Blocks referenced by unsynced queue ops must never be purged — the
    // queue is the authoritative pending-work source (Step 3; replaces the
    // per-row isSync flag, which sync-acks no longer maintain).
    const pendingIds = await getPendingBlockIds();

    // ── Find root page blocks in IDB that the API no longer knows about ──────
    const orphans = await appDB.blocks
      .where("workspaceId")
      .equals(workspaceId)
      .filter(
        (b) =>
          (b.parentType === "workspace" || b.parentType === "workarea") &&
          b.status === "alive" &&
          !apiPageIds.has(b._id) &&
          !pendingIds.has(b._id)
      )
      .toArray();

    if (orphans.length === 0) {
      console.log("[IDB cleanup] No orphan root pages found — IDB is clean");
      return;
    }

    console.log(
      `%c[IDB cleanup] 🗑️ Found ${orphans.length} orphan root page(s) to purge`,
      "color: #f87171; font-weight: bold;",
      orphans.map((b) => b._id)
    );

    const blockIdsToDelete: string[] = [];
    const pageCacheIdsToDelete: string[] = [];

    for (const pageBlock of orphans) {
      const pageId = pageBlock._id;

      // Use pageCache to find content blocks that belong to this page.
      // Only delete content blocks with no pending sync work — never touch
      // blocks referenced by an unsynced queue op (not yet on the server).
      const cacheEntry = await appDB.pageCache.get(pageId);
      if (cacheEntry) {
        for (const blockId of cacheEntry.blockIds) {
          if (blockId === pageId) continue; // page block handled below
          const block = await appDB.blocks.get(blockId);
          if (block && !pendingIds.has(blockId)) {
            blockIdsToDelete.push(blockId);
          }
        }
        pageCacheIdsToDelete.push(pageId);
      }

      // Always remove the root page block itself (pending-free — confirmed
      // by the candidate filter above)
      blockIdsToDelete.push(pageId);
    }

    // ── Single atomic transaction ─────────────────────────────────────────────
    await appDB.transaction("rw", [appDB.blocks, appDB.pageCache], async () => {
      if (blockIdsToDelete.length > 0) {
        await appDB.blocks.bulkDelete(blockIdsToDelete);
      }
      if (pageCacheIdsToDelete.length > 0) {
        await appDB.pageCache.bulkDelete(pageCacheIdsToDelete);
      }
    });

    console.log(
      `%c[IDB cleanup] ✅ Purged ${orphans.length} orphan root page(s), ${blockIdsToDelete.length} block(s) total`,
      "color: #4ade80; font-weight: bold;"
    );
  } catch (err) {
    console.warn("[IDB cleanup] purgeOrphanRootPages failed (non-fatal):", err);
  }
}

// ── Limits ────────────────────────────────────────────────────────────────────

// Production: MAX_PAGES = 30, MAX_BLOCKS = 5000, TTL_MS = 30 * 24 * 60 * 60 * 1000
const MAX_PAGES  = 30;
const MAX_BLOCKS = 5000; 
const TTL_MS     = 30 * 24 * 60 * 60 * 1000; // 30 days

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Record (or refresh) that a page was just accessed and which blocks were
 * fetched for it. Call this every time a remote fetch completes for a page.
 *
 * @param pageId           The page block _id (editorKey).
 * @param blockIds         All block IDs returned for this page by the API.
 * @param serverUpdatedAt  Optional: the server's page block updatedAt as a ms timestamp.
 *                         When provided (full fetch), stored as lastKnownServerUpdatedAt
 *                         so Effect A can skip unnecessary re-fetches next time.
 *                         When omitted, the existing lastKnownServerUpdatedAt is preserved.
 * @param lastEditedBy     Optional: the page block's lastEditedBy, stored alongside
 *                         the display stamp below.
 *
 * Safe to call from any context — never throws.
 */
export async function touchPageCache(
  pageId: string,
  blockIds: string[],
  serverUpdatedAt?: number,
  lastEditedBy?: PageCacheRecord["lastEditedBy"],
): Promise<void> {
  try {
    // put() replaces the whole record, so spread the existing one first —
    // anything not named below would otherwise be silently dropped.
    const existing = await appDB.pageCache.get(pageId);
    await appDB.pageCache.put({
      ...existing,
      pageId,
      lastAccessedAt: Date.now(),
      blockIds,
      lastKnownServerUpdatedAt: serverUpdatedAt ?? existing?.lastKnownServerUpdatedAt,
      // A full fetch carries the page block itself, so its updatedAt is this
      // page's own edit time and its author is current. The only path that can
      // set both authoritatively — including clearing an author the server no
      // longer reports.
      ...(serverUpdatedAt !== undefined
        ? { lastEditedAt: serverUpdatedAt, lastEditedBy, lastEditedByAt: serverUpdatedAt }
        : {}),
    });
    console.log(
      `%c[IDB cache] ✅ touchPageCache — pageId: ${pageId} | blocks: ${blockIds.length} | serverUpdatedAt: ${serverUpdatedAt ? new Date(serverUpdatedAt).toISOString() : "unchanged"}`,
      "color: #4ade80; font-weight: bold;"
    );
  } catch (err) {
    console.warn("[IDB eviction] touchPageCache failed:", err);
  }
}

/**
 * Stamp a page's edit time (and author, when known) from a LOCAL edit, before
 * any sync ack. Creates the entry when the page was never opened, and leaves
 * `lastKnownServerUpdatedAt` alone: that field gates fetch skipping and must
 * only ever carry a server time. Never throws.
 */
export async function recordLocalPageEdit(
  pageId: string,
  editedAt: number,
  editedBy: PageCacheRecord["lastEditedBy"] | null,
): Promise<void> {
  try {
    const existing = await appDB.pageCache.get(pageId);
    await appDB.pageCache.put({
      pageId,
      lastAccessedAt: editedAt,
      blockIds: [],
      ...existing,
      lastEditedAt: editedAt,
      ...(editedBy ? { lastEditedBy: editedBy, lastEditedByAt: editedAt } : {}),
    });
  } catch (err) {
    console.warn("[IDB cache] recordLocalPageEdit failed (non-fatal):", err);
  }
}

/**
 * Update ONLY the lastAccessedAt field in an existing pageCache entry.
 *
 * Called when a page is opened but the full fetch is skipped (the timestamp
 * check or unsynced-ops guard decided IDB is already current). Without this,
 * a frequently-revisited-but-unchanged page keeps a stale lastAccessedAt and
 * looks cold to LRU eviction — touchPageCache only runs on a full fetch.
 *
 * Silently no-ops when there is no pageCache entry (page never fully fetched).
 *
 * Safe to call from any context — never throws.
 */
export async function refreshPageCacheAccess(pageId: string): Promise<void> {
  try {
    const existing = await appDB.pageCache.get(pageId);
    if (!existing) {
      // Page was never cached — nothing to update.
      return;
    }
    await appDB.pageCache.put({
      ...existing,
      lastAccessedAt: Date.now(),
    });
  } catch (err) {
    console.warn("[IDB cache] refreshPageCacheAccess failed (non-fatal):", err);
  }
}

/**
 * Update ONLY the lastKnownServerUpdatedAt field in an existing pageCache entry.
 *
 * Called by SyncWatcher after a successful batch-update API call. The server
 * bumps the page's updatedAt when any child block changes — writing that value
 * back here means the next time Effect A opens this page, the timestamp check
 * will see IDB is already current and skip the full block fetch.
 *
 * Silently no-ops when there is no pageCache entry (page never fully fetched).
 *
 * Safe to call from any context — never throws.
 */
export async function refreshPageCacheTimestamp(
  pageId: string,
  serverUpdatedAt: number
): Promise<void> {
  try {
    const existing = await appDB.pageCache.get(pageId);
    if (!existing) {
      // Page was never cached — nothing to update.
      return;
    }
    await appDB.pageCache.put({
      ...existing,
      lastKnownServerUpdatedAt: serverUpdatedAt,
    });
    console.log(
      `%c[IDB cache] ✅ refreshPageCacheTimestamp — pageId: ${pageId} | serverUpdatedAt: ${new Date(serverUpdatedAt).toISOString()}`,
      "color: #4ade80; font-weight: bold;"
    );
  } catch (err) {
    console.warn("[IDB cache] refreshPageCacheTimestamp failed (non-fatal):", err);
  }
}

/**
 * Record that this page was just edited, for display.
 *
 * Kept apart from `refreshPageCacheTimestamp`: that one feeds the fetch-skip
 * decision, and its callers pass ids that are not always the page's own.
 *
 * `editedBy` is omitted by a local sync ack, which has no user context. That is
 * what `lastEditedByAt` is for — it stays behind `lastEditedAt`, and a reader
 * past that point knows the newer edit was local rather than crediting whoever
 * the server last named.
 *
 * Writes `lastKnownServerUpdatedAt` itself instead of leaving it to a parallel
 * `refreshPageCacheTimestamp` call: every writer here is a read-modify-write on
 * one row, so two of them in flight at once let the later put carry the
 * earlier's stale value — which silently froze this field in testing. Both
 * facts hold at a sync ack anyway: the cache is current as of this timestamp,
 * and this is when the page was edited.
 *
 * Silently no-ops when there is no pageCache entry. Never throws.
 */
export async function recordPageEditStamp(
  pageId: string,
  editedAt: number,
  editedBy?: PageCacheRecord["lastEditedBy"],
): Promise<void> {
  try {
    const existing = await appDB.pageCache.get(pageId);
    if (!existing) return;
    await appDB.pageCache.put({
      ...existing,
      lastKnownServerUpdatedAt: editedAt,
      lastEditedAt: editedAt,
      ...(editedBy ? { lastEditedBy: editedBy, lastEditedByAt: editedAt } : {}),
    });
  } catch (err) {
    console.warn("[IDB cache] recordPageEditStamp failed (non-fatal):", err);
  }
}

/**
 * Bulk equivalent of `refreshPageCacheTimestamp` — updates many pageCache entries
 * in ONE transaction. Same per-page semantics (no-op when a page was never
 * cached), never throws. Collapses N transactions into one so a large batch
 * doesn't fan out into N pageCache writes.
 */
export async function refreshPageCacheTimestamps(
  pageIds: string[],
  serverUpdatedAt: number
): Promise<void> {
  if (pageIds.length === 0) return;
  try {
    await appDB.transaction("rw", appDB.pageCache, async () => {
      await Promise.all(
        pageIds.map(async (pageId) => {
          const existing = await appDB.pageCache.get(pageId);
          if (!existing) return; // never cached — nothing to update
          await appDB.pageCache.put({
            ...existing,
            lastKnownServerUpdatedAt: serverUpdatedAt,
          });
        }),
      );
    });
  } catch (err) {
    console.warn("[IDB cache] refreshPageCacheTimestamps failed (non-fatal):", err);
  }
}

/**
 * Run LRU eviction. Call this after touchPageCache completes.
 * Fully async and non-blocking — the caller should not await this.
 *
 * @param currentPageId  The page currently open — never evicted.
 */
export async function runEviction(currentPageId: string): Promise<void> {
  try {
    // ── Load all cache records sorted oldest-first ─────────────────────────
    const allRecords = await appDB.pageCache
      .orderBy("lastAccessedAt")
      .toArray();

    const totalBlocks = await appDB.blocks.count();

    console.group(
      `%c[IDB eviction] 🔍 runEviction started — currentPage: ${currentPageId}`,
      "color: #60a5fa; font-weight: bold;"
    );
    console.log(`📦 Cached pages : ${allRecords.length} / ${MAX_PAGES}`);
    console.log(`🧱 Total blocks : ${totalBlocks} / ${MAX_BLOCKS}`);
    console.log(
      "📋 All cached pages (oldest → newest):",
      allRecords.map((r) => ({
        pageId      : r.pageId,
        blockCount  : r.blockIds.length,
        lastAccessed: formatInstant(r.lastAccessedAt, { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        isCurrent   : r.pageId === currentPageId,
      }))
    );

    // Partition: current page is untouchable
    const evictable = allRecords.filter((r) => r.pageId !== currentPageId);
    // Stamp-only entries (a cell edit on a row never opened) hold no blocks:
    // they neither count toward the page limit nor free anything if evicted.
    const holdsBlocks = (r: PageCacheRecord) => r.blockIds.length > 0;

    // ── Determine which pages to evict ────────────────────────────────────
    const toEvict = new Set<string>();

    // Rule 1 — TTL
    const ttlCutoff = Date.now() - TTL_MS;
    for (const record of evictable) {
      if (record.lastAccessedAt < ttlCutoff) {
        toEvict.add(record.pageId);
      }
    }
    console.log(
      `⏱️  Rule 1 (TTL ${TTL_MS / 1000}s) — expired pages: ${toEvict.size}`,
      [...toEvict]
    );

    // Rule 2 — page count
    const pagesAfterTTL = allRecords.filter((r) => holdsBlocks(r) && !toEvict.has(r.pageId)).length;
    if (pagesAfterTTL > MAX_PAGES) {
      const excess = pagesAfterTTL - MAX_PAGES;
      let evicted = 0;
      for (const record of evictable) {
        if (evicted >= excess) break;
        if (holdsBlocks(record) && !toEvict.has(record.pageId)) {
          toEvict.add(record.pageId);
          evicted++;
        }
      }
      console.log(
        `📄 Rule 2 (page count > ${MAX_PAGES}) — added ${evicted} more page(s) to evict set`,
        [...toEvict]
      );
    } else {
      console.log(`📄 Rule 2 (page count) — within limit (${pagesAfterTTL}/${MAX_PAGES}), skipped`);
    }

    // Rule 3 — block count
    if (totalBlocks > MAX_BLOCKS) {
      let projectedFree = 0;
      for (const record of evictable) {
        if (toEvict.has(record.pageId)) projectedFree += record.blockIds.length;
      }

      if (totalBlocks - projectedFree > MAX_BLOCKS) {
        const before = toEvict.size;
        for (const record of evictable) {
          if (totalBlocks - projectedFree <= MAX_BLOCKS) break;
          if (holdsBlocks(record) && !toEvict.has(record.pageId)) {
            toEvict.add(record.pageId);
            projectedFree += record.blockIds.length;
          }
        }
        console.log(
          `🧱 Rule 3 (block count > ${MAX_BLOCKS}) — added ${toEvict.size - before} more page(s), projected freed: ${projectedFree} blocks`,
          [...toEvict]
        );
      } else {
        console.log(`🧱 Rule 3 (block count) — projected free of ${projectedFree} blocks is enough, skipped`);
      }
    } else {
      console.log(`🧱 Rule 3 (block count) — within limit (${totalBlocks}/${MAX_BLOCKS}), skipped`);
    }

    // ── Reference check ───────────────────────────────────────────────────
    if (toEvict.size > 0) {
      const survivingBlockIds = new Set<string>();
      for (const record of allRecords) {
        if (!toEvict.has(record.pageId)) {
          for (const blockId of record.blockIds) {
            survivingBlockIds.add(blockId);
          }
        }
      }

      const protected_: string[] = [];
      for (const pageId of [...toEvict]) {
        if (survivingBlockIds.has(pageId)) {
          toEvict.delete(pageId);
          protected_.push(pageId);
        }
      }

      if (protected_.length > 0) {
        console.log(
          `🛡️  Reference check — protected ${protected_.length} child page(s) still referenced by a cached parent:`,
          protected_
        );
      } else {
        console.log("🛡️  Reference check — no protected pages, all candidates are safe to evict");
      }
    }

    if (toEvict.size === 0) {
      console.log("✅ Nothing to evict — all candidates were protected or limits not reached");
      console.groupEnd();
      return;
    }

    console.log(`🗑️  Final evict set (${toEvict.size} page(s)):`, [...toEvict]);

    // ── Collect block IDs to delete ───────────────────────────────────────
    // IMPORTANT: never delete the page block itself (_id === pageId).
    // The page block holds the title, icon, and parentId that the sidebar,
    // useLiveBlock, and parent page references all depend on. Only content
    // blocks (paragraphs, headings, boards, etc.) are safe to evict.
    const blockIdsToDelete: string[] = [];
    const skippedUnsynced: string[] = [];
    const skippedPageBlocks: string[] = [];

    // Queue-derived pending set (Step 3): blocks referenced by unsynced ops
    // are not yet on the server and must never be evicted.
    const pendingIds = await getPendingBlockIds();

    for (const pageId of toEvict) {
      const cacheRecord = await appDB.pageCache.get(pageId);
      if (!cacheRecord) continue;

      for (const blockId of cacheRecord.blockIds) {
        // Never evict the page block itself — sidebar and hooks depend on it
        if (blockId === pageId) {
          skippedPageBlocks.push(blockId);
          continue;
        }
        const block = await appDB.blocks.get(blockId);
        if (block && !pendingIds.has(blockId)) {
          blockIdsToDelete.push(blockId);
        } else if (block) {
          skippedUnsynced.push(blockId);
        }
      }
    }

    if (skippedPageBlocks.length > 0) {
      console.log(
        `🏠 Kept ${skippedPageBlocks.length} page block(s) in IDB — sidebar & hooks depend on them`,
        skippedPageBlocks
      );
    }
    if (skippedUnsynced.length > 0) {
      console.log(
        `⚠️  Skipped ${skippedUnsynced.length} unsynced block(s) (pending queue ops) — will sync to server first`,
        skippedUnsynced
      );
    }
    console.log(`🗑️  Content blocks to delete: ${blockIdsToDelete.length}`);

    // ── Apply eviction in a single atomic transaction ─────────────────────
    await appDB.transaction("rw", [appDB.blocks, appDB.pageCache], async () => {
      if (blockIdsToDelete.length > 0) {
        await appDB.blocks.bulkDelete(blockIdsToDelete);
      }
      if (toEvict.size > 0) {
        await appDB.pageCache.bulkDelete([...toEvict]);
      }
    });

    const blocksAfter = await appDB.blocks.count();
    const pagesAfter  = await appDB.pageCache.count();

    console.log(
      `%c[IDB eviction] ✅ Done — evicted ${toEvict.size} page(s), ${blockIdsToDelete.length} block(s) | IDB now: ${pagesAfter} pages, ${blocksAfter} blocks`,
      "color: #4ade80; font-weight: bold;"
    );
    console.groupEnd();

  } catch (err) {
    console.warn("[IDB eviction] runEviction error (non-fatal):", err);
    console.groupEnd();
  }
}
